import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDownload, FiX, FiSmartphone } = FiIcons;

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if it's iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show install prompt after 10 seconds
      setTimeout(() => {
        if (!isInstalled) {
          setShowInstallPrompt(true);
        }
      }, 10000);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // For iOS devices, show custom prompt after 10 seconds
    if (iOS && !isInstalled) {
      setTimeout(() => {
        setShowInstallPrompt(true);
      }, 10000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled]);

  const handleInstallClick = async () => {
    if (!deferredPrompt && !isIOS) return;

    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      
      setDeferredPrompt(null);
    }
    
    setShowInstallPrompt(false);
  };

  const handleClosePrompt = () => {
    setShowInstallPrompt(false);
  };

  // Don't show if already installed
  if (isInstalled) return null;

  return (
    <AnimatePresence>
      {showInstallPrompt && (
        <motion.div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClosePrompt}
        >
          <motion.div
            className="bg-white dark:bg-luxury-charcoal rounded-lg shadow-xl max-w-md w-full p-6 relative"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleClosePrompt}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>

            {/* App Icon */}
            <div className="flex justify-center mb-4">
              <img
                src="https://i.ibb.co/q3wdWB9T/Nashmi-barber-shop-Logo.jpg"
                alt="Nashmi Barber Shop"
                className="w-16 h-16 rounded-full border-2 border-luxury-gold"
              />
            </div>

            {/* Content */}
            <div className="text-center">
              <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
                Install Nashmi Barber App
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Get quick access to our services, book appointments, and stay updated with our latest offers!
              </p>

              {/* Features */}
              <div className="space-y-2 mb-6 text-left">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-luxury-gold rounded-full"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-300">Quick booking access</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-luxury-gold rounded-full"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-300">Offline browsing</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-luxury-gold rounded-full"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-300">Push notifications for offers</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-luxury-gold rounded-full"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-300">Fast and secure</span>
                </div>
              </div>

              {/* Install Instructions */}
              {isIOS ? (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-6">
                  <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
                    To install on iOS:
                  </p>
                  <ol className="text-xs text-blue-700 dark:text-blue-300 list-decimal list-inside space-y-1">
                    <li>Tap the Share button in Safari</li>
                    <li>Scroll down and tap "Add to Home Screen"</li>
                    <li>Tap "Add" to install the app</li>
                  </ol>
                </div>
              ) : null}

              {/* Action Buttons */}
              <div className="flex space-x-3">
                {!isIOS && (
                  <button
                    onClick={handleInstallClick}
                    className="flex-1 bg-luxury-gold text-black py-3 px-4 rounded-lg font-semibold hover:bg-luxury-darkGold transition-colors duration-300 flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiDownload} className="w-5 h-5" />
                    <span>Install App</span>
                  </button>
                )}
                
                <button
                  onClick={handleClosePrompt}
                  className={`${isIOS ? 'flex-1' : 'flex-1'} border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 py-3 px-4 rounded-lg font-semibold hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-300`}
                >
                  {isIOS ? 'Got it!' : 'Maybe Later'}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PWAInstallPrompt;